import { addDays, differenceInCalendarDays, format } from 'date-fns';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import {
  DetailContext,
  FormContext,
  FormLocalDateField,
  FormTextField,
  useFormSelector,
} from '@eas/common-web';
import { Deadline } from '@models';

export function DeadlineDialog() {
  const { source } = useContext(DetailContext);
  const { setFieldValue } = useContext(FormContext);

  const deliveryDate = source?.data?.deliveryDate;

  const { startDate, endDate, duration } = useFormSelector(
    (values: Deadline) => ({
      startDate: values.startDate,
      endDate: values.endDate,
      duration: values.duration,
    })
  );

  const formattedDeliveryDate = deliveryDate
    ? format(new Date(deliveryDate), 'dd.MM.yyyy')
    : '';

  const handleStartDateChange = (value: unknown) => {
    const newStart = value
      ? new Date(value as string)
      : deliveryDate
      ? new Date(deliveryDate)
      : null;
    const currentEnd = endDate ? new Date(endDate) : null;

    if (newStart && currentEnd) {
      const newDuration = differenceInCalendarDays(currentEnd, newStart);
      if (newDuration !== duration) {
        setFieldValue('duration', newDuration);
      }
    }
  };

  const handleEndDateChange = (value: unknown) => {
    const newEnd = value ? new Date(value as string) : null;
    const currentStart = startDate
      ? new Date(startDate)
      : deliveryDate
      ? new Date(deliveryDate)
      : null;

    if (currentStart && newEnd) {
      const newDuration = differenceInCalendarDays(newEnd, currentStart);
      if (newDuration !== duration) {
        setFieldValue('duration', newDuration);
      }
    }
  };

  const handleDurationChange = (value: unknown) => {
    const newDuration =
      typeof value === 'number' ? value : parseInt(value as string, 10);
    const currentStart = startDate
      ? new Date(startDate)
      : deliveryDate
      ? new Date(deliveryDate)
      : null;

    if (currentStart && !isNaN(newDuration)) {
      const newEnd = addDays(currentStart, newDuration)
        .toISOString()
        .split('T')[0];
      if (newEnd !== endDate) {
        setFieldValue('endDate', newEnd);
      }
    }
  };

  return (
    <>
      {deliveryDate && (
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          <FormattedMessage
            id="ES__ES_FILES__DEADLINE_FORM_FIELDS__DESCRIPTION"
            defaultMessage="Pozn. pokud nebude vyplněno datum začátku lhůty, bude jako datum začátku lhůty použito datum doručení: {deliveryDate}"
            values={{ deliveryDate: formattedDeliveryDate }}
          />
        </Typography>
      )}
      <FormLocalDateField
        name="startDate"
        label={
          <FormattedMessage
            id="ES__ES_FILES__DEADLINE_FORM_FIELDS__FIELD_LABEL__START_DATE"
            defaultMessage="Začátek"
          />
        }
        notifyChange={handleStartDateChange}
      />
      <FormLocalDateField
        name="endDate"
        label={
          <FormattedMessage
            id="ES__ES_FILES__DEADLINE_FORM_FIELDS__FIELD_LABEL__END_DATE"
            defaultMessage="Konec"
          />
        }
        notifyChange={handleEndDateChange}
      />
      <FormTextField
        type="number"
        name="duration"
        label={
          <FormattedMessage
            id="ES__ES_FILES__DEADLINE_FORM_FIELDS__FIELD_LABEL__DURATION"
            defaultMessage="Počet dnů"
          />
        }
        notifyChange={handleDurationChange}
      />
    </>
  );
}
